import { Icon } from '@iconify/react'
import { observable, observe } from '@legendapp/state'
import { Computed, useObservable } from '@legendapp/state/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLoaderData } from '@tanstack/react-router'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { toast } from 'react-toastify'

import {
  Button,
  Card,
  CardContent,
  DialogComponent,
  DropdownMenuComponent,
} from '~in-app/components'
import { Option } from '~in-app/components/MultiSelectDropdown'
import {
  FilterDropdownProps,
  TableContextProvider,
  TableState$,
} from '~in-app/components/table/TableContext'
import { auth$ } from '~in-app/legend-state'
import { deleteOperatorGroup, fetchAllDivision, fetchDistributorList, fetchOperatorGroupList, fetchOperatorList, fetchRolesList, updateOperatorGroupStatus } from '~in-app/shareable/index.api'
import { DistributorRoot, Division } from '~in-app/shareable/index.types'
import { Operators } from '~in-app/shareable/operator/types'
import { Roles } from '~in-app/shareable/roles/types'
import { OperatorGroup } from '~in-app/types'

export const Route = createFileRoute('/console/operator-groups/list')({
  component: OperatorGroupPage,
  loader: async () => {
    let clientIds: Option[] = []
    let operatorIds: Option[] = []
    let divisionIds: Option[] = []
    let roleIds: Option[] = []

    const privilegeValue = auth$.privileges.get()
    const defaultDivisionList = privilegeValue.has('0.4.0.0')
      ? await fetchAllDivision()
      : {} as { divisions: Division[] }
    const flattenDivisions = (
      divisions: any[],
      parentId: string | null = null,
    ): any[] => {
      return divisions.reduce((acc: any[], division: any) => {
        acc.push({
          value: division.id,
          label: division.name,
          parentId: parentId,
        })
        if (division.mychilds && division.mychilds.length > 0) {
          acc = acc.concat(flattenDivisions(division.mychilds, division.id))
        }
        return acc
      }, [])
    }
    divisionIds = flattenDivisions(defaultDivisionList.divisions)

    const defaultOperatorList = privilegeValue.has('0.3.2.0')
      ? await fetchOperatorList()
      : {} as { list: Operators[] }
    operatorIds = defaultOperatorList
      ? defaultOperatorList?.list?.map((item: any) => ({
        value: item.id,
        label: `${item?.firstName || ''} ${item?.lastName || ''}`,
      }))
      : []

    const defaultRolesList = privilegeValue.has('0.3.5.0')
      ? await fetchRolesList()
      : {} as { list: Roles[] }

    roleIds = defaultRolesList
      ? defaultRolesList?.list?.map((item: any) => ({
        value: item.id,
        label: item.name,
      }))
      : []

    const defaultDistributorList = privilegeValue.has('0.3.7.0')
      ? await fetchDistributorList()
      : {} as { list: DistributorRoot[] }

    clientIds = defaultDistributorList
      ? defaultDistributorList?.list?.map((item: any) => ({
        value: item.id,
        label: item.name,
      }))
      : []
    return { clientIds, operatorIds, divisionIds, roleIds, privilegeValue }
  },
  staticData: {
    privileges: ['0.3.3.0'],
  },
})
const columnHelper = createColumnHelper<OperatorGroup>()
const defaultState$ = observable<{
  sortList: { id: string, desc: boolean }[]
  pagination: number
  pageSize: number
  queries: string
  operatorIds: string[] | undefined
  roleIds: string[] | undefined
  clientIds: string | undefined
  divisionIds: string[] | undefined
  status: string | undefined
  isChange: boolean
  isOpenDeleteOperatorGroup: boolean
  deleteOperatorGroupId: string | undefined
  deleteOperatorGroupName: string | undefined
}>({
  sortList: [
    { id: 'name', desc: true },
    { id: 'status', desc: true },
  ],
  divisionIds: undefined,
  operatorIds: undefined,
  roleIds: undefined,
  clientIds: undefined,
  pagination: 0,
  pageSize: 20,
  queries: '',
  status: undefined,
  isChange: false,
  isOpenDeleteOperatorGroup: false,
  deleteOperatorGroupName: undefined,
  deleteOperatorGroupId: undefined,
})
function OperatorGroupPage() {
  const { clientIds, operatorIds, divisionIds, roleIds, privilegeValue } = useLoaderData({ from: '/console/operator-groups/list' })

  const navigate = Route.useNavigate()
  const dropdownItems = (value: OperatorGroup) => {
    return [
      {
        label: 'Edit',
        onClick: () => navigate({ to: `/console/operator-groups/edit/${value.id}` }),
        disabled: !privilegeValue?.has('0.3.3.2'),
        icon: (
          <Icon
            height="16"
            icon="lucide:edit-2"
            width="16"
          />
        ),
      },
      {
        label: value.active ? 'Deactivate' : 'Activate',
        onClick: () => handleUpdateStatus(value.id, !value.active),
        disabled: privilegeValue?.has('0.3.3.3') ? value.staticFlag : true,
        icon: value.active
          ? (
              <Icon
                height="16"
                icon="lucide:circle-x"
                width="16"
              />
            )
          : (
              <Icon
                height="16"
                icon="lucide:circle-check"
                width="16"
              />
            ),
      },
      {
        label: 'Delete',
        onClick: () => handleDeleteItem(value),
        disabled: privilegeValue?.has('0.3.3.3') ? value.staticFlag : true,
        icon: (
          <Icon
            height="16"
            icon="lucide:trash-2"
            width="16"
          />
        ),
      },
    ]
  }

  const columns = [
    columnHelper.accessor('id', {
      cell: info => info.getValue(),
      header: () => <span>ID</span>,
    }),
    columnHelper.accessor('name', {
      cell: info => info.getValue(),
      header: () => <span>Name</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('active', {
      cell: info => (info.getValue() ? 'Active' : 'Inactive'),
      header: () => <span>Status</span>,
      enableSorting: true,
      size: 110,
    }),
    columnHelper.accessor('desp', {
      cell: info => info.getValue(),
      header: () => <span>Description</span>,
      enableSorting: true,
      size: 150,
    }),
    columnHelper.accessor('remarks', {
      cell: info => info.getValue(),
      header: () => <span>Remarks</span>,
      enableSorting: true,
      size: 150,
    }),
    columnHelper.accessor('staticFlag', {
      cell: info => (info.getValue() ? 'Yes' : 'No'),
      header: () => <span>Static</span>,
      enableSorting: true,
      size: 120,
    }),
    columnHelper.accessor('createdOn', {
      cell: info => dayjs(info.getValue()).format('YYYY-MM-DD'),
      header: () => <span>Created On</span>,
      enableSorting: true,
      size: 140,
    }),
    columnHelper.accessor('updatedOn', {
      cell: info => dayjs(info.getValue()).format('YYYY-MM-DD'),
      header: () => <span>Updated On</span>,
      enableSorting: true,
      size: 140,
    }),
    columnHelper.accessor('action', {
      cell: info => (
        <span>
          <DropdownMenuComponent
            preventClose
            buttonIcon={<Icon icon="lucide:ellipsis-vertical" />}
            className="rounded-md bg-transparent p-2 hover:opacity-100"
            items={dropdownItems(info.row.original)}
          />
        </span>
      ),
      header: () => <span />,
      enableSorting: false,
      size: 50,
    }),
  ]

  const state$ = useObservable(defaultState$)

  const filterDropdownItems: FilterDropdownProps[] = [
    {
      label: 'Search', // New dropdown for controlling sidebar
      type: 'text',
      value: new URLSearchParams(window.location.search).get('queries') || '',
      result: new URLSearchParams(window.location.search).get('queries') || '',
    },
    {
      label: 'Clients',
      type: 'dropdown',
      isMultiSelect: false,
      isSplitRow: true,
      options: clientIds,
      result: new URLSearchParams(window.location.search).get('clientIds') || undefined,
      value: new URLSearchParams(window.location.search).get('clientIds') || undefined,
    },
    {
      label: 'Status',
      type: 'dropdown',
      isMultiSelect: false,
      isSplitRow: true,
      options: [{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }],
      result: new URLSearchParams(window.location.search).get('status') || undefined,
      value: new URLSearchParams(window.location.search).get('status') || undefined,

    },
    {
      label: 'Division',
      type: 'dropdown',
      isMultiSelect: true,
      options: divisionIds,
      value: divisionIds.filter(divisions => new URLSearchParams(window.location.search).get('divisionIds')?.split(',').includes(divisions.value)),
      result: divisionIds.filter(divisions => new URLSearchParams(window.location.search).get('divisionIds')?.split(',').includes(divisions.value)),
    },
    {
      label: 'Operators',
      type: 'dropdown',
      isMultiSelect: true,
      options: operatorIds,
      value: operatorIds.filter(operators => new URLSearchParams(window.location.search).get('operatorIds')?.split(',').includes(operators.value)),
      result: operatorIds.filter(operators => new URLSearchParams(window.location.search).get('operatorIds')?.split(',').includes(operators.value)),
    },
    {
      label: 'Roles',
      type: 'dropdown',
      isMultiSelect: true,
      options: roleIds,
      value: roleIds.filter(roles => new URLSearchParams(window.location.search).get('roleIds')?.split(',').includes(roles.value)),
      result: roleIds.filter(roles => new URLSearchParams(window.location.search).get('roleIds')?.split(',').includes(roles.value)),
    },
  ]
  const tableState$ = useObservable<TableState$<OperatorGroup>>(() => ({
    id: 'operator-group-table',
    isSortable: true,
    data: [],
    columns: columns,
    hideColumns: ['id'],
    sort: state$.sortList.get(),
    totalItem: 0,
    totalPage: 0,
    pageSize: state$.pageSize.get(),
    pagination: state$.pagination.get(),
    pageIndex: 0,
    filters: filterDropdownItems,
    onApply: (value: any) => {
      const searchParams: Record<string, any> = {}

      const textFilter = value.find((item: any) => item.label === 'Search')
      const textValue = textFilter?.result || ''
      state$.queries.set(textValue)
      searchParams.queries = textValue

      const divisionFilter = value.find((item: Option) => item.label === 'Division')
      const divisionValue = divisionFilter?.result || []
      if (divisionValue.length > 0) {
        state$.divisionIds.set(divisionValue.map((item: { value: any }) => item.value))
        searchParams.divisionIds = divisionValue.map((item: { value: any }) => item.value).join(',')
      }
      else {
        state$.divisionIds.set(undefined)
        searchParams.divisionIds = undefined
      }

      const roleFilter = value.find(
        (item: Option) => item.label === 'Roles',
      )
      const roleValue = roleFilter?.result || []
      if (roleValue.length > 0) {
        state$.roleIds.set(
          roleValue.map((item: { value: any }) => item.value),
        )
        searchParams.roleIds = roleValue.map((item: { value: any }) => item.value).join(',')
      }
      else {
        state$.roleIds.set(undefined)
        searchParams.roleIds = undefined
      }

      const operatorFilter = value.find(
        (item: Option) => item.label === 'Operators',
      )
      const operatorValue = operatorFilter?.result || []
      if (operatorValue.length > 0) {
        state$.operatorIds.set(
          operatorValue.map((item: { value: any }) => item.value),
        )
        searchParams.operatorIds = operatorValue.map((item: { value: any }) => item.value).join(',')
      }
      else {
        state$.operatorIds.set(undefined)
        searchParams.operatorIds = undefined
      }

      const clientFilter = value.find(
        (item: Option) => item.label === 'Clients',
      )
      const clientValue = clientFilter?.result
      state$.clientIds.set(clientValue)
      searchParams.clientIds = clientValue

      const statusFilter = value.find(
        (item: Option) => item.label === 'Status',
      )
      const statusValue
        = statusFilter?.result === 'inactive' ? false : statusFilter?.result === 'active' ? true : undefined
      state$.status.set(statusValue as any)
      searchParams.status = statusValue

      state$.isChange.set(!state$.isChange.get())
      navigate({
        search: (prev) => {
          const updatedSearch: Record<string, any> = { ...prev, ...searchParams } // Explicitly type as Record<string, any>

          // Remove empty or undefined keys safely
          if (!textFilter?.result) delete updatedSearch.queries
          if (!(divisionValue.length > 0)) delete updatedSearch.divisionIds
          if (!(roleValue.length > 0)) delete updatedSearch.roleIds
          if (!(operatorValue.length > 0)) delete updatedSearch.operatorIds
          if (!clientValue) delete updatedSearch.clientIds
          if (!statusValue) delete updatedSearch.status

          return updatedSearch
        },
      })
    },
    setSort: (value: any) => {
      state$.sortList.set(value)
    },
    setPageSize: (value: any) => {
      state$.pageSize.set(value)
      navigate({ search: prev => ({ ...prev, pageSize: value }) })
    },
    setPagination: (value: any) => {
      state$.pagination.set(value)
      navigate({ search: prev => ({ ...prev, page: value + 1 }) })
    },
  }))

  const queryClient = useQueryClient()

  useEffect(() => {
    state$.pagination.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('page')
        return match ? parseInt(match, 10) - 1 : 0
      })(),
    )
    state$.pageSize.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('pageSize')
        return match ? parseInt(match, 10) : 20
      })(),
    )
    const filteredQueries = new URLSearchParams(window.location.search).get('queries') || ''
    state$.operatorIds.set(new URLSearchParams(window.location.search).get('operatorIds')?.split(','))
    state$.divisionIds.set(new URLSearchParams(window.location.search).get('divisionIds')?.split(','))
    state$.roleIds.set(new URLSearchParams(window.location.search).get('roleIds')?.split(','))
    state$.clientIds.set(new URLSearchParams(window.location.search).get('clientIds') ?? undefined)
    state$.status.set(new URLSearchParams(window.location.search).get('status') ?? undefined)
    state$.queries.set(filteredQueries)
    const disposer = observe(state$, (e) => {
      if (e.previous !== undefined && e.value !== undefined) {
        refetch()
      }
    }, { immediate: false })

    return disposer
  }, [])

  const { refetch } = useQuery({
    queryKey: ['operator-group-list'],
    queryFn: async ({ signal }) => {
      const response = await fetchOperatorGroupList({
        query: {
          page: state$.pagination.get() || 0,
          size: state$.pageSize.get() || 20,
          queries: state$.queries.get() || undefined,
          divisionIds: state$?.divisionIds?.get() ? (state$?.divisionIds?.get() ?? []).join(',') : undefined,
          roleIds: state$?.roleIds?.get() ? (state$?.roleIds?.get() ?? []).join(',') : undefined,
          operatorIds: state$?.operatorIds?.get() ? (state$?.operatorIds?.get() ?? []).join(',') : undefined,
          clientId: state$?.clientIds?.get(),
          active: state$?.status?.get(),
          sort: state$.sortList
            .get()
            .map((sortByVal) => {
              return `${sortByVal.id},${sortByVal.desc ? 'desc' : 'asc'}`
            })
            .join(','),
        },
      })
      const newOperatorGroupList = response?.list.map((item: any) => ({
        ...item,
        action: '',
      }))
      tableState$.data.set(newOperatorGroupList || [])
      tableState$.totalItem.set(response?.totalItem || 0)
      tableState$.totalPage.set(response?.totalPage || 0)
      if ((response?.totalPage ?? 0) < (state$.pagination.get() + 1)) {
        state$.pagination.set(0)
        navigate({ search: prev => ({ ...prev, page: 1 }) })
      }
      return response
    },
  })

  const updateOperatorGroupStatusFunc = () => {
    const mutation = useMutation({
      mutationFn: ({ id, status }: { id: string, status: boolean }) => updateOperatorGroupStatus(id, status),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['operator-group-list'] })
        toast.success('Operator Group status updated successfully', { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
        toast.error(`Error updating Operator Group status: ${error.message}`, { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
    })

    return mutation
  }
  const updateMutation = updateOperatorGroupStatusFunc()

  const handleUpdateStatus = (id: string, status: boolean) => {
    updateMutation.mutate({ id, status })
  }

  const useDeleteOperatorGroups = () => {
    const mutation = useMutation({
      mutationFn: deleteOperatorGroup,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['operator-group-list'] })
        toast.success('Operator Group deleted successfully', { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const deleteMutation = useDeleteOperatorGroups()

  const handleDeleteItem = (value: OperatorGroup) => {
    state$.deleteOperatorGroupId.set(value.id)
    state$.deleteOperatorGroupName.set(value.name)
    state$.isOpenDeleteOperatorGroup.set(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between p-2">
        <span className="mb-4 ml-4 text-2xl font-bold"> Operator Groups Page</span>

        <Computed>
          {
            () => (
              <DialogComponent
                isOpen={state$.isOpenDeleteOperatorGroup.get()}
                submitButtonLabel="Delete"
                title={`Are You Sure To Delete this '${state$.deleteOperatorGroupName.get()}' Operator Group?`}
                onClose={() => { state$.isOpenDeleteOperatorGroup.set(false), state$.deleteOperatorGroupName.set(undefined), state$.deleteOperatorGroupId.set(undefined) }}
                onSubmit={
                  () => {
                    const deleteOperatorGroupId = state$.deleteOperatorGroupId.get()
                    if (deleteOperatorGroupId) {
                      deleteMutation.mutate(deleteOperatorGroupId as string)
                      state$.deleteOperatorGroupId.set(undefined)
                      state$.isOpenDeleteOperatorGroup.set(false)
                      state$.deleteOperatorGroupName.set(undefined)
                    }
                  }
                }
              />
            )
          }
        </Computed>

        <Button
          className="rounded-md border border-slate-400 bg-transparent font-medium text-muted-foreground hover:opacity-100"
          color="muted"
          disabled={!privilegeValue.has('0.3.3.1')}
          onClick={() => navigate({ to: '/console/operator-groups/add' })}
        >
          Add
        </Button>
      </div>

      <div className="p-2">
        <Card className="mt-4">
          <CardContent>
            <div>
              <TableContextProvider state$={tableState$} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
